import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import {
  Dimmer, Loader, Message, Table,
} from 'semantic-ui-react';

import '../assets/theme.css';

import CancelledGuaranteePurchasesListV2 from '../views/CancelledGuaranteePurchases/ListV2';
import GuaranteeReinstate from '../views/CancelledGuaranteePurchases/ReinstateGuaranteeForm';
import Search from '../views/Shared/Search';
import Pagination from '../views/Shared/Pagination';
import AuditModal from '../components/AuditModal';
import client from '../apollo';
import useExternalPurchasesList from '../hooks/useExternalPurchasesList';
import externalCancelledPurchasesQuery from '../graphql/externalCancelledPurchasesQuery';
import cancelledPurchaseQuery from '../graphql/cancelledPurchaseQuery';
import cancelledGuaranteePurchasesAuditsQuery from '../graphql/cancelledGuaranteePurchasesAuditsQuery';
import reinstateGuaranteePurchaseMutation from '../graphql/reinstateGuaranteePurchaseMutation';
import { DEFAULT_PAGE_SIZE } from '../constants';

const ACTION_UNAVAILABLE = 'This action is unavailable for this purchase.';

const buildVariables = ({
  page, categoryCode, vin, dealer,
}) => ({
  page,
  limit: DEFAULT_PAGE_SIZE,
  categoryCode,
  vin,
  dealer,
});

const renderAuditRow = auditRecord => {
  return (
    <>
      {auditRecord.event === 'update'
        && `${auditRecord.versionAuthor} updated the Cancelled Guarantee - ${
          auditRecord.itemType
        } details`}
      {auditRecord.event === 'create'
        && `${auditRecord.versionAuthor} created the Cancelled Guarantee - ${
          auditRecord.itemType
        }`}
      {auditRecord.event === 'destroy'
        && `${auditRecord.versionAuthor} Destroyed Cancelled Guarantee - ${
          auditRecord.itemType
        }`}
    </>
  );
};

const renderChangesetRow = (auditRecord, change, key) => {
  if (key !== 'updated_at'
    && auditRecord.event === 'update'
    && change[1] !== null
    && change[1] !== '') {
    return (
      <Table.Row key={key}>
        <Table.Cell>
          <span>
            set{' '}
            <span style={{ fontWeight: 'bold' }}>
              {key}{' '}
            </span>
            from{' '}
            <span style={{ fontWeight: 'bold' }}>
              {' '}
              {change[0] || 'nil'}{' '}
            </span>
            to{' '}
            <span style={{ fontWeight: 'bold' }}>
              {change[1] || 'nil'}{' '}
            </span>
          </span>
        </Table.Cell>
      </Table.Row>
    );
  }
  return null;
};

const CancelledGuaranteePurchasesV2 = ({ searchFields, reinstateMutation }) => {
  const {
    items,
    error,
    refetch,
    start,
    end,
    isLastPage,
    isInitialLoading,
    isPaginationLoading,
    nextHandler,
    previousHandler,
    onSearchSubmit,
  } = useExternalPurchasesList({
    query: externalCancelledPurchasesQuery,
    responseField: 'externalCancelledPurchases',
    buildVariables,
    dealerField: 'dealerNumber',
  });

  const [openModal, setOpenModal] = useState(null);
  const [legacyPurchase, setLegacyPurchase] = useState(null);
  const [auditPurchaseId, setAuditPurchaseId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [flashMessage, setFlashMessage] = useState(null);
  const [formError, setFormError] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const handleMessageDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const closeModal = useCallback(() => {
    setOpenModal(null);
    setLegacyPurchase(null);
    setAuditPurchaseId(null);
    setFormError(null);
    setFormSubmitting(false);
  }, []);

  const onSelectGuarantee = useCallback(async(action, row) => {
    setActionError(null);
    const { cancelledPurchaseId } = row;

    if (action === 'Status History') {
      if (!cancelledPurchaseId) {
        setActionError(ACTION_UNAVAILABLE);
        return;
      }
      setAuditPurchaseId(cancelledPurchaseId);
      setOpenModal('audit');
      return;
    }

    if (action !== 'Reinstate Guarantee') return;

    if (!cancelledPurchaseId) {
      setActionError(ACTION_UNAVAILABLE);
      return;
    }

    try {
      const result = await client.query({
        query: cancelledPurchaseQuery,
        variables: { id: cancelledPurchaseId },
        fetchPolicy: 'network-only',
      });
      const record = result.data && result.data.cancelledPurchase;
      if (!record) {
        setActionError(ACTION_UNAVAILABLE);
        return;
      }
      setFormError(null);
      setFormSubmitting(false);
      setLegacyPurchase(record);
      setOpenModal('reinstate');
    } catch (fetchError) {
      setActionError(ACTION_UNAVAILABLE);
    }
  }, []);

  const onReinstateFormSubmitHandler = useCallback(
    async values => {
      setFormSubmitting(true);
      try {
        const { data } = await reinstateMutation({
          variables: { reinstateInput: { ...values } },
        });
        const { success, errors } = data.reinstateGuaranteePurchase;
        if (!success) {
          setFormSubmitting(false);
          setFormError(errors);
          return;
        }
        setFlashMessage('Successfully Reinstate Guarantee Purchase');
        closeModal();
        refetch();
      } catch (mutationError) {
        setFormSubmitting(false);
        setFormError('Error while reinstate Guarantee Purchase');
      }
    },
    [closeModal, refetch, reinstateMutation]
  );

  return (
    <div>
      {error && (
        <Message
          negative
          content={error.message || 'An error occurred while loading data.'}
        />
      )}
      {actionError && <Message negative content={actionError} />}
      {flashMessage && (
        <Message
          onDismiss={handleMessageDismiss}
          content={flashMessage}
          positive
        />
      )}
      <Search
        onSubmit={onSearchSubmit}
        searchFields={searchFields}
        searchOptions={['vin', 'dealerNumber']}
        multipleSearches
      />
      <Dimmer.Dimmable dimmed={isInitialLoading}>
        <Dimmer active={isInitialLoading} inverted>
          <Loader data-testid='loading-overlay'>Loading...</Loader>
        </Dimmer>

        <CancelledGuaranteePurchasesListV2
          onSelectGuarantee={onSelectGuarantee}
          items={items}
        />

        <Pagination
          start={start}
          end={end}
          nextHandler={nextHandler}
          previousHandler={previousHandler}
          loading={isPaginationLoading}
          lastPage={isLastPage}
          pageSize={DEFAULT_PAGE_SIZE}
        />
      </Dimmer.Dimmable>

      {legacyPurchase && openModal === 'reinstate' && (
        <GuaranteeReinstate
          isReinstateModalOpen
          onCloseHandler={closeModal}
          onSubmitHandler={onReinstateFormSubmitHandler}
          cancelledPurchase={legacyPurchase}
          error={formError}
          submitting={formSubmitting}
        />
      )}

      {auditPurchaseId && openModal === 'audit' && (
        <AuditModal
          header='Status History - Canceled Purchases'
          fetchAuditRecordQuery={cancelledGuaranteePurchasesAuditsQuery}
          queryVariables={{ cancelledPurchaseId: parseInt(auditPurchaseId, 10) }}
          auditResponseAttribute='cancelledPurchaseAudits'
          renderAuditRow={renderAuditRow}
          renderChangesetRow={renderChangesetRow}
          isAuditModalOpen
          onCloseHandler={closeModal}
        />
      )}
    </div>
  );
};

CancelledGuaranteePurchasesV2.propTypes = {
  searchFields: PropTypes.object.isRequired,
  reinstateMutation: PropTypes.func,
};

CancelledGuaranteePurchasesV2.defaultProps = {
  reinstateMutation: null,
};

const mapStateToProps = state => {
  const {
    data: { searchFields },
  } = state;
  return { searchFields };
};

export default compose(
  connect(mapStateToProps),
  graphql(reinstateGuaranteePurchaseMutation, { name: 'reinstateMutation' })
)(CancelledGuaranteePurchasesV2);
